<template>
	<div class="py-0.5 w-full border-gray-200" :class="dividerClass">
		<button
			type="button"
			class="py-1.5 w-full flex items-center justify-between animated-disclosure__button"
			:class="titleClass"
			:aria-expanded="isOpen"
			:aria-controls="panelId"
			@click="toggle"
		>
			{{ title }}
			<span>
				<ChevronDownIcon
					v-if="!isOpen"
					class="block h-4 w-4"
					aria-hidden="true"
				/>
				<ChevronUpIcon v-else class="block h-4 w-4" aria-hidden="true" />
			</span>
		</button>

		<!--
			A closed panel is only height 0, so `inert` keeps its links out of
			the tab order and the accessibility tree.
		-->
		<div
			:id="panelId"
			ref="panelRef"
			class="overflow-hidden transition-[height] duration-300 ease-in-out"
			:style="{ height }"
			:inert="!isOpen"
		>
			<div class="py-2">
				<slot v-if="$slots.default" />
				<template v-else-if="content">
					{{ content }}
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";

interface Props {
	title: string;
	content?: string;
	/** Weight/size classes for the question row. */
	titleClass?: string;
	/** Border classes for the line under each item. */
	dividerClass?: string;
}
const {
	title,
	content,
	titleClass = "font-bold",
	dividerClass = "border-b"
} = defineProps<Props>();

const panelId = useId();
const isOpen = ref(false);
const height = ref("0px");
const panelRef = ref<HTMLElement | null>(null);

const toggle = async () => {
	isOpen.value = !isOpen.value;
};

watch(isOpen, async (open) => {
	await nextTick();

	if (panelRef.value) {
		if (open) {
			height.value = panelRef.value.scrollHeight + "px";

			setTimeout(() => {
				if (isOpen.value) height.value = "auto";
			}, 300);
		} else {
			if (height.value === "auto") {
				height.value = panelRef.value.scrollHeight + "px";
				await nextTick();
			}
			requestAnimationFrame(() => {
				height.value = "0px";
			});
		}
	}
});
</script>
